'use client';

import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import {
    MessageSquare,
    Zap,
    Globe,
    Bot,
    Shield,
    Smartphone,
    Link as LinkIcon,
    Lock,
    Users,
    Award,
    Smile,
    Mail,
    BarChart,
    Code,
    Github,
    RefreshCw,
} from 'lucide-react';

const features = [
    {
        category: 'Modern Discussions',
        items: [
            {
                icon: MessageSquare,
                title: 'Conversations, not pages',
                desc: 'Why break conversations into awkward and arbitrary pages? We’ve replaced all that with the power of just-in-time loading. Want to read more? Keep scrolling down.',
            },
            {
                icon: Zap,
                title: 'Chat in real time',
                desc: 'Create channels for community members to chat more informally. Chat messages can be quoted in topics where the discussion can continue over time.',
            },
            {
                icon: MessageSquare,
                title: 'Simple, but with context',
                desc: 'Talktrail is a simple, flat forum, where replies flow down the page. Expand context at the bottom and top of each post to reveal the full conversation.',
            },
            {
                icon: LinkIcon,
                title: 'Links automatically expand',
                desc: 'Want to share a cool link? Just paste it on a single line. Links to Wikipedia, YouTube, GitHub, and hundreds of other sites automatically expand.',
            },
        ],
    },
    {
        category: 'AI & Intelligence',
        items: [
            {
                icon: Globe,
                title: 'AI translations',
                desc: 'Translate the contents of your community using AI or manual translations. Available for up to 49 languages, with more continually supported.',
            },
            {
                icon: Bot,
                title: 'Empowering possibilities with AI',
                desc: 'Our one-stop solution for integrating Artificial Intelligence to your Talktrail community, making it easier to use and moderate.',
            },
            {
                icon: BarChart,
                title: 'Summarize topics',
                desc: 'Use the Summarize button to condense long topics to just the most interesting and popular posts.',
            },
        ],
    },
    {
        category: 'Community & Trust',
        items: [
            {
                icon: Shield,
                title: 'Trust system',
                desc: 'As members become trusted regulars over time, they earn abilities to help maintain their community.',
            },
            {
                icon: Users,
                title: 'Community moderation',
                desc: 'Let the community suppress spam and dangerous content, and amicably resolve disputes.',
            },
            {
                icon: Award,
                title: 'Badges',
                desc: 'Encourage positive community behaviors through the included set of badges, or add your own custom badges.',
            },
        ],
    },
    {
        category: 'Mobile & Access',
        items: [
            {
                icon: Smartphone,
                title: 'Born mobile, born to touch',
                desc: 'Designed for high resolution touch devices, with a built-in mobile layout. Read or post from your laptop, tablet, and phone.',
            },
            {
                icon: Lock,
                title: 'Single sign-on',
                desc: 'Seamlessly integrate Talktrail with your existing site’s login system with easy, robust single sign on.',
            },
            {
                icon: Mail,
                title: 'Reply via email',
                desc: 'When you aren’t active on the website, notifications will be automatically sent via email. Reply from anywhere, on any device.',
            },
        ],
    },
    {
        category: 'Developer & Admin',
        items: [
            {
                icon: Code,
                title: 'Comprehensive API',
                desc: 'Anything you see on screen, you can also do via an API call. Talktrail is a JavaScript app that talks to our API.',
            },
            {
                icon: Github,
                title: '100% open source',
                desc: 'Incorporate Talktrail into your site with complete confidence – the code belongs to everyone.',
            },
            {
                icon: RefreshCw,
                title: 'One-click updates',
                desc: 'Automatic notification of new versions in the dashboard, with a one-click web update process.',
            },
        ],
    },
];

const FeatureCard = ({ item, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        viewport={{ once: true }}
        className="group relative p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 hover:scale-[1.02]"
    >
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-transparent to-cyan-600/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
        <div className="relative z-10">
            <div className="w-12 h-12 mb-4 rounded-xl bg-gradient-to-br from-violet-500/20 to-cyan-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <item.icon className="w-6 h-6 text-white group-hover:text-cyan-300 transition-colors" />
            </div>
            <h3 className="text-xl font-bold mb-2 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-violet-400 group-hover:to-cyan-400 transition-all">
                {item.title}
            </h3>
            <p className="text-zinc-400 leading-relaxed group-hover:text-zinc-300">
                {item.desc}
            </p>
        </div>
    </motion.div>
);

export default function FeaturesPage() {
    return (
        <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
            {/* Background Ambience */}
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
            </div>

            <Navbar />

            <main className="flex-1 relative z-10 pt-24 pb-16">
                {/* Header */}
                <section className="container px-4 md:px-6 mb-20 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-4xl mx-auto space-y-6"
                    >
                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
                            Amazing features your <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                                community will love
                            </span>
                        </h1>
                        <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
                            We’re reimagining what a modern discussion platform should be.
                            Powered by the OriginTrail DKG for verifiable knowledge assets.
                        </p>
                    </motion.div>
                </section>

                {/* Features Categories */}
                <div className="container px-4 md:px-6 space-y-24">
                    {features.map((section, idx) => (
                        <section key={idx} className="space-y-8">
                            <motion.h2
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className="text-3xl font-bold text-white border-l-4 border-violet-500 pl-4"
                            >
                                {section.category}
                            </motion.h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {section.items.map((item, itemIdx) => (
                                    <FeatureCard key={itemIdx} item={item} index={itemIdx} />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>

                {/* More List */}
                <section className="container px-4 md:px-6 mt-32">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="rounded-3xl border border-white/10 bg-zinc-900/50 p-8 md:p-12 backdrop-blur-xl"
                    >
                        <h2 className="text-3xl font-bold mb-8 text-center">
                            Plus so much more!
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 text-zinc-400">
                            {[
                                'SEO optimized',
                                'Automatic backups',
                                'Blog integration',
                                'Email invitations',
                                'Dark mode',
                                'Anonymous posting',
                                'File attachments',
                                'Hide spoilers',
                                'Embeddable polls',
                                'Desktop notifications',
                                'Auto-save drafts',
                                'Post revision history',
                                'Personal messaging',
                                'Drag & drop uploads',
                                'Wiki posts',
                                'Site analytics',
                            ].map((text, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                                    <span>{text}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
